import { useState } from 'react'
import { formatWallTime } from '../../lib/appointments'
import { Chip } from './Chip'

/** Every quarter hour a boutique might plausibly start an appointment. */
const FIRST = 6 * 60
const LAST = 22 * 60
const STEP = 15

function wall(minutes: number): string {
  return `${String(Math.floor(minutes / 60)).padStart(2, '0')}:${String(minutes % 60).padStart(2, '0')}`
}

const TIMES: readonly string[] = Array.from({ length: (LAST - FIRST) / STEP + 1 }, (_, i) => wall(FIRST + i * STEP))

/**
 * A start time on the showroom's clock, `HH:MM`, from a list of quarter
 * hours printed the way the rest of the console prints a time.
 *
 * Not `<input type="time">`: the browser draws that in its OWN language and
 * clock, so a console pinned to English (`lib/boutique.ts`) showed "下午03:00"
 * on a tablet set to Chinese — the very failure that module exists to stop.
 * tperent's `SlotTimePicker` is a grid of the shop's slots for the same
 * reason; with no slot templates here, the grid is the quarter hours. A time
 * already off the grid — pushed from Koalendar at 2:10 — stays selectable as
 * itself.
 *
 * `presets` are the boutique's usual start times (Settings → Appointment
 * times), drawn as toggles, one tap each. The long list appears only behind
 * the last toggle, "Custom", for the appointment that starts at 10:30 — or
 * already when the time being edited is not one of the usual ones.
 */
export function TimeSelect({
  value,
  onChange,
  label = 'Time',
  presets = [],
}: {
  readonly value: string
  readonly onChange: (time: string) => void
  readonly label?: string
  readonly presets?: readonly string[]
}) {
  const isPreset = presets.includes(value)
  // Custom stays open once chosen, even before a time is picked from it.
  const [custom, setCustom] = useState(() => value !== '' && !isPreset)
  const times = value && !TIMES.includes(value) ? [...TIMES, value].sort() : TIMES
  const showList = presets.length === 0 || custom
  const select = (
    <select
      className="st-select"
      aria-label={presets.length > 0 ? `Custom ${label.toLowerCase()}` : label}
      value={presets.length > 0 && isPreset ? '' : value}
      onChange={(event) => onChange(event.target.value)}
    >
      <option value="" disabled>
        Pick a time
      </option>
      {times.map((time) => (
        <option key={time} value={time}>
          {formatWallTime(time)}
        </option>
      ))}
    </select>
  )
  if (presets.length === 0) return select
  return (
    <div className="flex flex-col gap-2">
      <div className="flex flex-wrap gap-1.5" role="group" aria-label={label}>
        {presets.map((time) => (
          <Chip
            key={time}
            pressed={!custom && value === time}
            onClick={() => {
              setCustom(false)
              onChange(time)
            }}
          >
            {formatWallTime(time)}
          </Chip>
        ))}
        <Chip
          pressed={custom}
          onClick={() => {
            setCustom(true)
            if (isPreset) onChange('')
          }}
        >
          Custom
        </Chip>
      </div>
      {showList ? select : null}
    </div>
  )
}
